import { Component } from 'Shared/frontend/widgets/Component';
import { StylesProvider, IStyleDetails, useStylePath, useStyleDetails } from 'Shared/frontend/widgets/StylesProvider';
import { siteTheme } from 'Shared/frontend/SettingsProvider';
import { createCssFromSettings } from 'Shared/frontend/widgets/createCssFromSettings';
import { locationMatches } from 'Shared/frontend/widgets/locationMatches';


export function Styles(P: {styles:{category:string,type:string,params?:any}[],removeStyles:{category:string,type:string}[],classes:string[]})
{
	const path = useStylePath();

	const parentStyles = useStyleDetails();

	/* Add new styles. We prepend the current path to their location expressions. */
	const calcStyles = () => {
		const flatPath = path.map(e => e.key);

		const trimmedParent = parentStyles().filter(e =>
			!(P.removeStyles ?? []).some(r => r.category === e.category && r.type === e.tag)
		);
		const styleDetails = resolveStyleDetails(flatPath,P.styles);

		return [...trimmedParent, ...styleDetails] as IStyleDetails[];
	};

	return (
		<StylesProvider path={path} styleDetails={calcStyles}> 
			<Component {...P.content} />
		</StylesProvider>
	);
}

function resolveField(value: any, params: any, atTop?: boolean): any {
	return typeof value === 'function' ? value(params, atTop) : value;
}

function applyRule(rule, params: any, atTop: boolean|undefined, css: any, settings: any)
{
	const ruleCss = {...resolveField(rule.css, params, atTop)};
	const ruleSettings = {};
	for (const [fieldName, fieldValue] of Object.entries(rule)) {
		const value = resolveField(fieldValue, params, atTop);
		if (value !== undefined)
			createCssFromSettings(fieldName, value, ruleCss, ruleSettings);
	}
	Object.assign(css, ruleCss);
	Object.assign(settings, ruleSettings);
}

export function resolveNavStyles(styleDetails: IStyleDetails[], atTop: boolean): any
{
	const navPath = [{key: 'nav'}];
	let css = {};
	let settings = {};

	for (const detail of styleDetails) {
		const style = siteTheme().styles[detail.category]?.[detail.tag];
		if (!style) continue;

		for (const rule of style.rules ?? []) {
			const location = resolveField(rule.location, detail.params, atTop);
			if (locationMatches([...detail.pathWhenAdded, ...location], navPath))
				applyRule(rule, detail.params, atTop, css, settings);
		}
	}
	delete (settings as any).location;
	return {...settings, css};
}

export function resolveFooterStyles(styleDetails: IStyleDetails[]): any
{
	const footerPath = [{key: 'footer'}];
	let css = {};
	let settings = {};

	for (const detail of styleDetails) {
		const style = siteTheme().styles[detail.category]?.[detail.tag];
		if (!style) continue;

		for (const rule of style.rules ?? []) {
			const location = resolveField(rule.location, detail.params, undefined);
			if (locationMatches([...detail.pathWhenAdded, ...location], footerPath))
				applyRule(rule, detail.params, undefined, css, settings);
		}
	}
	delete (settings as any).location;
	return {...settings, css};
}

export function resolveStyleDetails(flatPath, styles:{category:string,type:string,params?:any}[]): IStyleDetails[]
{
	const styleDetails: IStyleDetails[] = [];

	for (const style of styles ?? []) {
		const styleDef = siteTheme().styles[style.category]?.[style.type];
		if (!styleDef)
			log.warn(`Style not found: ${style.category}/${style.type}`)

		const params = style.params;
		const resolved = [];

		for (const rule of styleDef?.rules ?? []) {
			const location = resolveField(rule.location, params, undefined);
			const css = {...resolveField(rule.css, params, undefined)};
			const settings = {};

			for (const [fieldName, fieldValue] of Object.entries(rule)) {
				const value = resolveField(fieldValue, params, undefined);
				if (value !== undefined)
					createCssFromSettings(fieldName, value, /*mod*/css, /*mod*/settings);
			}

			resolved.push({location, settings, css});
		}
		styleDetails.push({category: style.category, tag: style.type, pathWhenAdded: flatPath, params, rules: resolved});
	}
	return styleDetails;
}

